import { Component, EventEmitter, OnInit } from '@angular/core';
import {
  DwButtonCommand,
  DwButtonConfig,
  DwModalButtonStandardConfigs,
  DwModalConfig,
  DwModalHandler,
  DwModalResult,
  DwModalService,
  DwModalSize
} from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import {
  ReportDashboardGroupDefinition,
  ReportDashboardGroupTabDefinition
} from '../../models/report-dashboard-group-definition.model';
import {
  ReportDashboardConfigRecord,
  ReportDashboardWidgetConfigRecord
} from '../../models/report-dashboard-config-record.model';
import {
  ReportDashboardCreateModalComponent,
  ReportDashboardCreateModalResult
} from '../report-dashboard-create-modal/report-dashboard-create-modal.component';

interface DashboardCatalogOption {
  label: string;
  value: string;
  display: string;
  iconClass?: string;
  isActive: boolean;
  contextNames: string[];
  config: any;
}

interface DashboardSelectionOption {
  label: string;
  value: string;
  action: 'add' | 'clone';
  dashboardKey: string;
}

export interface ReportDashboardGroupModalData {
  groupDisplay: string;
  definition: ReportDashboardGroupDefinition;
  dashboardRecords: ReportDashboardConfigRecord[];
  widgetRecords?: ReportDashboardWidgetConfigRecord[];
}

export interface ReportDashboardPendingClone {
  sourceDashboardKey: string;
  record: Partial<ReportDashboardConfigRecord>;
}

export interface ReportDashboardGroupModalResult {
  definition: ReportDashboardGroupDefinition;
  dashboardClones: ReportDashboardPendingClone[];
}

@Component({
  selector: 'app-report-dashboard-group-modal',
  templateUrl: './report-dashboard-group-modal.component.html',
  styleUrls: ['./report-dashboard-group-modal.component.scss']
})
export class ReportDashboardGroupModalComponent implements OnInit, DwModalHandler {
  modalConfig: DwModalConfig<ReportDashboardGroupModalData>;
  closeModal = new EventEmitter<DwModalResult<ReportDashboardGroupModalResult>>();

  definition: ReportDashboardGroupDefinition;
  dashboardOptions: DashboardCatalogOption[] = [];
  pendingClones: ReportDashboardPendingClone[] = [];
  requiredContextNames: string[] = [];
  selectedDashboardAction: string;
  errorMessage: string;

  constructor(private modalService: DwModalService) { }

  ngOnInit(): void {
    this.definition = this.clone(this.modalConfig.data.definition);
    this.definition.tabs = this.definition.tabs || [];
    this.definition.contextNames = this.definition.contextNames || [];
    this.dashboardOptions = (this.modalConfig.data.dashboardRecords || [])
      .map(record => this.toDashboardOption(record))
      .sort((a, b) => a.display.localeCompare(b.display));
    this.refreshRequiredContexts();
  }

  get dashboardSelectionOptions(): DashboardSelectionOption[] {
    const configuredKeys = this.definition.tabs.map(tab => tab.dashboardKey);
    const addOptions = this.dashboardOptions
      .filter(option => option.isActive && configuredKeys.indexOf(option.value) < 0)
      .map(option => ({
        label: option.label,
        value: `add:${option.value}`,
        action: 'add' as 'add',
        dashboardKey: option.value
      }));
    const cloneOptions = this.definition.tabs
      .map(tab => ({ tab, dashboard: this.dashboardOptions.find(option => option.value === tab.dashboardKey) }))
      .filter(item => !!item.dashboard?.isActive)
      .map(item => ({
        label: `${item.tab.display} — Clone`,
        value: `clone:${item.tab.dashboardKey}`,
        action: 'clone' as 'clone',
        dashboardKey: item.tab.dashboardKey
      }));
    return [...addOptions, ...cloneOptions];
  }

  get selectedDashboardActionType(): 'add' | 'clone' {
    return this.dashboardSelectionOptions
      .find(option => option.value === this.selectedDashboardAction)?.action || 'add';
  }

  addDashboard(): void {
    const selection = this.dashboardSelectionOptions
      .find(option => option.value === this.selectedDashboardAction);
    if (selection?.action === 'clone') {
      this.cloneDashboard(selection.dashboardKey);
      return;
    }

    const dashboard = this.dashboardOptions.find(option => option.value === selection?.dashboardKey);
    if (!dashboard || !dashboard.isActive) {
      this.errorMessage = 'Choose an active dashboard to add.';
      return;
    }
    if (this.definition.tabs.some(tab => tab.dashboardKey === dashboard.value)) {
      this.errorMessage = `${dashboard.display} is already in this group.`;
      return;
    }
    this.definition.tabs.push({
      dashboardKey: dashboard.value,
      display: dashboard.display,
      iconClass: dashboard.iconClass
    });
    this.refreshRequiredContexts();
    if (!this.definition.defaultDashboardKey) {
      this.definition.defaultDashboardKey = dashboard.value;
    }
    this.selectedDashboardAction = null;
    this.errorMessage = null;
  }

  onTabsChanged(): void {
    this.refreshRequiredContexts();
  }

  onTabRemoved(tab: ReportDashboardGroupTabDefinition): void {
    if (!this.definition.tabs.length) {
      this.definition.tabs.push(tab);
      this.errorMessage = 'A dashboard group must contain at least one tab.';
      return;
    }
    const pendingCloneIndex = this.pendingClones.findIndex(clone =>
      clone.record.DashboardKey === tab.dashboardKey);
    if (pendingCloneIndex >= 0) {
      this.pendingClones.splice(pendingCloneIndex, 1);
      this.dashboardOptions = this.dashboardOptions.filter(option => option.value !== tab.dashboardKey);
    }
    this.refreshRequiredContexts();
    if (this.definition.defaultDashboardKey === tab.dashboardKey) {
      this.definition.defaultDashboardKey = this.definition.tabs[0]?.dashboardKey;
    }
    this.errorMessage = null;
  }

  dashboardDisplay(dashboardKey: string): string {
    return this.dashboardOptions.find(option => option.value === dashboardKey)?.display || dashboardKey;
  }

  dashboardIsActive(dashboardKey: string): boolean {
    return this.dashboardOptions.find(option => option.value === dashboardKey)?.isActive === true;
  }

  setDefaultDashboard(dashboardKey: string): void {
    this.definition.defaultDashboardKey = dashboardKey;
  }

  onContextNamesChanged(contextNames: string[]): void {
    this.definition.contextNames = this.uniqueStrings([
      ...(contextNames || []),
      ...this.requiredContextNames
    ]);
  }

  onTabVisibilityChanged(): void {
    this.refreshRequiredContexts();
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<ReportDashboardGroupModalResult>> {
    if (buttonConfig.command !== DwButtonCommand.saveAndClose) {
      return of({
        cancelled: true,
        closeModal: true,
        buttonCommand: buttonConfig.command
      } as DwModalResult<ReportDashboardGroupModalResult>);
    }

    const validationError = this.validate();
    if (validationError) {
      this.errorMessage = validationError;
      return of({ closeModal: false } as DwModalResult<ReportDashboardGroupModalResult>);
    }

    this.errorMessage = null;
    return of({
      cancelled: false,
      closeModal: true,
      buttonCommand: buttonConfig.command,
      data: {
        definition: this.definition,
        dashboardClones: this.pendingClones
      }
    } as DwModalResult<ReportDashboardGroupModalResult>);
  }

  private cloneDashboard(sourceDashboardKey: string): void {
    const source = this.dashboardOptions.find(option => option.value === sourceDashboardKey);
    const sourceTab = this.definition.tabs.find(tab => tab.dashboardKey === sourceDashboardKey);
    if (!source || !sourceTab) {
      this.errorMessage = 'The dashboard selected for cloning is no longer available.';
      return;
    }

    const dashboardKey = this.nextCloneKey(sourceDashboardKey);
    const modalConfig = new DwModalConfig();
    modalConfig.title = 'Clone Dashboard';
    modalConfig.component = ReportDashboardCreateModalComponent;
    modalConfig.buttons = DwModalButtonStandardConfigs.instance.SaveAndCancel;
    modalConfig.modalSize = DwModalSize.large;
    modalConfig.cloneData = true;
    modalConfig.data = {
      groupDisplay: this.modalConfig.data.groupDisplay,
      existingDashboardKeys: this.dashboardOptions.map(option => option.value),
      defaultDataSourceItemName: source.config?.dataSourceItemName,
      mode: 'clone',
      sourceDashboardDisplay: source.display,
      initialValues: {
        dashboardKey,
        dashboardDisplay: this.nextCloneDisplay(source.display),
        tabDisplay: this.nextCloneTabDisplay(sourceTab.display),
        title: this.nextCloneDisplay(source.config?.title || source.display),
        description: source.config?.description,
        iconClass: sourceTab.iconClass || source.config?.iconClass,
        dataSourceItemName: source.config?.dataSourceItemName,
        isActive: true
      }
    };

    this.modalService.showModal(modalConfig).subscribe(result => {
      if (result.cancelled || !result.data) {
        return;
      }
      this.addPendingClone(source, sourceTab, result.data as ReportDashboardCreateModalResult);
    });
  }

  private addPendingClone(
    source: DashboardCatalogOption,
    sourceTab: ReportDashboardGroupTabDefinition,
    values: ReportDashboardCreateModalResult
  ): void {
    const config = this.clone(source.config) || {};
    delete config.key;
    delete config.contextNames;
    config.title = values.title;
    this.setOptionalConfigValue(config, 'description', values.description);
    this.setOptionalConfigValue(config, 'iconClass', values.iconClass);
    this.setOptionalConfigValue(config, 'dataSourceItemName', values.dataSourceItemName);

    const pendingClone: ReportDashboardPendingClone = {
      sourceDashboardKey: source.value,
      record: {
        DashboardKey: values.dashboardKey,
        DashboardDisplay: values.dashboardDisplay,
        DashboardConfigJSON: JSON.stringify(config, null, 2),
        IsActive: values.isActive
      }
    };
    this.pendingClones.push(pendingClone);

    const clonedRecord = pendingClone.record as ReportDashboardConfigRecord;
    this.dashboardOptions.push(this.toDashboardOption(clonedRecord));
    this.dashboardOptions.sort((a, b) => a.display.localeCompare(b.display));
    this.definition.tabs.push({
      dashboardKey: values.dashboardKey,
      display: values.tabDisplay,
      iconClass: values.iconClass || undefined,
      visibleExpression: this.clone(sourceTab.visibleExpression)
    });
    if (!this.definition.defaultDashboardKey) {
      this.definition.defaultDashboardKey = values.dashboardKey;
    }
    this.selectedDashboardAction = null;
    this.refreshRequiredContexts();
    this.errorMessage = null;
  }

  private validate(): string {
    if (!this.definition.title?.trim()) {
      return 'Group title is required.';
    }
    if (!this.definition.tabs?.length) {
      return 'A dashboard group must contain at least one tab.';
    }
    if (this.definition.tabs.some(tab => !tab.dashboardKey || !tab.display?.trim())) {
      return 'Every tab requires a dashboard key and display name.';
    }
    const keys = this.definition.tabs.map(tab => tab.dashboardKey);
    if (new Set(keys).size !== keys.length) {
      return 'A dashboard can only appear once in a group.';
    }
    if (this.definition.defaultDashboardKey
      && keys.indexOf(this.definition.defaultDashboardKey) < 0) {
      return 'Default dashboard must reference one of the configured tabs.';
    }
    const missingContexts = this.requiredContextNames
      .filter(contextName => !(this.definition.contextNames || [])
        .some(selected => selected.toLowerCase() === contextName.toLowerCase()));
    if (missingContexts.length) {
      return `The group is missing contexts required by its dashboards: ${missingContexts.join(', ')}.`;
    }
    const missingDashboard = this.definition.tabs.find(tab =>
      !this.dashboardOptions.some(option => option.value === tab.dashboardKey && option.isActive)
    );
    if (missingDashboard) {
      return `Dashboard ${missingDashboard.dashboardKey} is missing or inactive. Remove it or reactivate its configuration.`;
    }
    return null;
  }

  private toDashboardOption(record: ReportDashboardConfigRecord): DashboardCatalogOption {
    let config: any = {};
    try {
      config = typeof record.DashboardConfigJSON === 'string'
        ? JSON.parse(record.DashboardConfigJSON)
        : record.DashboardConfigJSON || {};
    } catch (_error) {
      config = {};
    }
    const display = config.title || record.DashboardDisplay || record.DashboardKey;
    return {
      label: `${display} (${record.DashboardKey})${record.IsActive ? '' : ' — inactive'}`,
      value: record.DashboardKey,
      display,
      iconClass: config.iconClass,
      isActive: !!record.IsActive,
      contextNames: this.findDashboardContextNames(config),
      config
    };
  }

  private nextCloneKey(sourceKey: string): string {
    const existingKeys = this.dashboardOptions.map(option => option.value.toLowerCase());
    const base = `${sourceKey}-copy`;
    let candidate = base;
    let suffix = 2;
    while (existingKeys.indexOf(candidate.toLowerCase()) >= 0) {
      candidate = `${base}-${suffix++}`;
    }
    return candidate;
  }

  private nextCloneDisplay(sourceDisplay: string): string {
    const existingDisplays = this.dashboardOptions.map(option => option.display.toLowerCase());
    const base = `${sourceDisplay} Copy`;
    let candidate = base;
    let suffix = 2;
    while (existingDisplays.indexOf(candidate.toLowerCase()) >= 0) {
      candidate = `${base} ${suffix++}`;
    }
    return candidate;
  }

  private nextCloneTabDisplay(sourceDisplay: string): string {
    const existingDisplays = this.definition.tabs.map(tab => tab.display.toLowerCase());
    const base = `${sourceDisplay} Copy`;
    let candidate = base;
    let suffix = 2;
    while (existingDisplays.indexOf(candidate.toLowerCase()) >= 0) {
      candidate = `${base} ${suffix++}`;
    }
    return candidate;
  }

  private setOptionalConfigValue(config: any, key: string, value: string): void {
    if (value) {
      config[key] = value;
    } else {
      delete config[key];
    }
  }

  private findDashboardContextNames(config: any): string[] {
    const reusableWidgetConfigs = (config?.widgets || [])
      .filter(widget => !!widget?.widgetKey)
      .map(widget => this.modalConfig.data?.widgetRecords
        ?.find(record => record.WidgetKey === widget.widgetKey))
      .filter(record => !!record)
      .map(record => {
        try {
          return typeof record.WidgetConfigJSON === 'string'
            ? JSON.parse(record.WidgetConfigJSON)
            : record.WidgetConfigJSON;
        } catch (_error) {
          return null;
        }
      });
    return this.uniqueStrings([
      ...this.findContextNames(config),
      ...reusableWidgetConfigs.reduce(
        (contextNames, widgetConfig) => [...contextNames, ...this.findContextNames(widgetConfig)],
        [] as string[]
      )
    ]);
  }

  private refreshRequiredContexts(): void {
    const configuredKeys = this.definition?.tabs?.map(tab => tab.dashboardKey) || [];
    this.requiredContextNames = this.uniqueStrings([
      ...this.dashboardOptions
        .filter(option => configuredKeys.indexOf(option.value) >= 0)
        .reduce((contextNames, option) => [...contextNames, ...option.contextNames], [] as string[]),
      ...this.findContextNames(this.definition.tabs)
    ]);
    this.definition.contextNames = this.uniqueStrings([
      ...(this.definition.contextNames || []),
      ...this.requiredContextNames
    ]);
  }

  private findContextNames(config: any): string[] {
    const contextNames: string[] = [];

    const visit = (value: any): void => {
      if (!value || typeof value !== 'object') {
        return;
      }
      if (value.referenceType === 'context' && typeof value.contextName === 'string') {
        contextNames.push(value.contextName);
      }
      Object.keys(value).forEach(key => visit(value[key]));
    };
    visit(config);
    return this.uniqueStrings(contextNames);
  }

  private uniqueStrings(values: string[]): string[] {
    return (values || []).filter((value, index, source) => !!value
      && source.findIndex(item => item.toLowerCase() === value.toLowerCase()) === index);
  }

  private clone<T>(value: T): T {
    return value ? JSON.parse(JSON.stringify(value)) : value;
  }
}
